import React, { useCallback, useEffect, useState } from "react";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon, BarsArrowDownIcon, BarsArrowUpIcon } from "@heroicons/react/24/outline";
import { getAdminEvents, getAdminStats } from "../api/monitoringAPI";
import { Card, ErrorState, Empty, RefreshBtn, fmt } from "./monitoring/ui";

const EMPTY_FILTERS = { event_type: "", user_id: "", device_id: "", start_date: "", end_date: "" };

const inputCls =
  "w-full rounded-lg border border-border-primary bg-bg-primary px-3 py-2 text-sm text-text-primary " +
  "placeholder:text-text-muted focus:border-[#4F45E5] focus:outline-none focus:ring-2 focus:ring-[#4F45E5]/30";

const fmtTime = (iso) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—";

export const EventRow = ({ event }) => {
  const [open, setOpen] = useState(false);
  const payload = event.data || event.event_data;
  const summary = payload && typeof payload === "object" ? Object.entries(payload).slice(0, 3).map(([k, v]) => `${k}: ${typeof v === "object" ? "…" : v}`).join(" · ") : payload;
  return (
    <li>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} className="grid w-full grid-cols-[8.5rem_minmax(0,1fr)_auto] items-center gap-3 px-5 py-2.5 text-left text-sm hover:bg-bg-tertiary/50 md:grid-cols-[9.5rem_12rem_6rem_minmax(0,1fr)_1rem]">
        <span className="font-mono text-xs tabular-nums text-text-secondary">{fmtTime(event.timestamp || event.created_at || event.date)}</span>
        <span className="truncate font-medium text-text-primary">{event.event_type || event.type || "—"}</span>
        <span className="font-mono text-xs text-text-muted md:text-text-secondary">{event.user_id ? `#${event.user_id}` : "гость"}</span>
        <span className="hidden truncate text-xs text-text-muted md:block">{summary || ""}</span>
        <ChevronDownIcon className={`hidden h-4 w-4 text-text-muted transition-transform md:block ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-2 bg-bg-primary/60 px-5 py-3 text-xs">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-text-secondary">
            <span>
              user_id: <span className="font-mono text-text-primary">{event.user_id ?? "—"}</span>
            </span>
            <span>
              device_id: <span className="font-mono text-text-primary">{event.device_id ?? "—"}</span>
            </span>
            {event.platform && <span>platform: {event.platform}</span>}
          </div>
          <pre className="max-h-72 overflow-auto rounded-lg border border-border-primary bg-bg-card p-3 text-[11px] leading-relaxed text-text-primary">
            {JSON.stringify(payload ?? event, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
};

const EventsList = () => {
  const [form, setForm] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [limit, setLimit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [orderDir, setOrderDir] = useState("desc");
  const [eventTypes, setEventTypes] = useState([]);

  // Подсказки типов событий — из сводной статистики
  useEffect(() => {
    getAdminStats()
      .then((s) => setEventTypes((s.event_type_breakdown || []).map((e) => e.event_type).filter(Boolean)))
      .catch(() => {});
  }, []);

  // Ввод в фильтрах применяется с задержкой
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(form);
      setOffset(0);
    }, 400);
    return () => clearTimeout(t);
  }, [form]);

  const fetchEvents = useCallback(async () => {
    setState((s) => (s === "ready" ? "ready" : "loading"));
    setError(null);
    try {
      const params = { limit, offset, order_by: "timestamp", order_dir: orderDir };
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params[k] = v.trim();
      });
      const data = await getAdminEvents(params);
      const list = Array.isArray(data) ? data : data.items || data.events || [];
      setEvents(list);
      setTotal(Array.isArray(data) ? list.length : data.total ?? data.count ?? list.length);
      setState("ready");
    } catch (err) {
      console.error("Error fetching events:", err);
      setError(err.message);
      setState("error");
    }
  }, [filters, limit, offset, orderDir]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const hasFilters = Object.values(form).some(Boolean);

  return (
    <div className="space-y-4">
      <Card bodyClassName="p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <label className="col-span-2 block md:col-span-1 xl:col-span-2">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Тип события</span>
            <input list="event-types" value={form.event_type} onChange={set("event_type")} placeholder="screen_view, login…" className={inputCls} />
            <datalist id="event-types">
              {eventTypes.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">ID пользователя</span>
            <input value={form.user_id} onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value.replace(/\D/g, "") }))} inputMode="numeric" placeholder="1141" className={`${inputCls} font-mono`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">ID устройства</span>
            <input value={form.device_id} onChange={set("device_id")} placeholder="device id" className={`${inputCls} font-mono`} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">С</span>
            <input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">По</span>
            <input type="date" value={form.end_date} onChange={set("end_date")} className={inputCls} />
          </label>
        </div>
      </Card>

      {state === "error" ? (
        <ErrorState text="Не удалось загрузить события" detail={error} onRetry={fetchEvents} />
      ) : (
        <Card
          title={state === "ready" ? `${fmt(total)} событий` : "События"}
          subtitle="Нажмите на строку, чтобы увидеть данные события"
          actions={
            <div className="flex items-center gap-2">
              {hasFilters && (
                <button type="button" onClick={() => setForm(EMPTY_FILTERS)} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-text-secondary hover:bg-bg-tertiary">
                  <XMarkIcon className="h-4 w-4" /> Сбросить фильтры
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setOrderDir((d) => (d === "desc" ? "asc" : "desc"));
                  setOffset(0);
                }}
                title={orderDir === "desc" ? "Сначала новые" : "Сначала старые"}
                className="inline-flex items-center gap-1 rounded-lg border border-border-primary px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-bg-tertiary"
              >
                {orderDir === "desc" ? <BarsArrowDownIcon className="h-4 w-4" /> : <BarsArrowUpIcon className="h-4 w-4" />}
                {orderDir === "desc" ? "Новые" : "Старые"}
              </button>
              <RefreshBtn onClick={fetchEvents} loading={state === "loading"} />
            </div>
          }
        >
          {state === "loading" && events.length === 0 ? (
            <ul className="divide-y divide-border-primary">
              {Array.from({ length: 10 }).map((_, i) => (
                <li key={i} className="flex animate-pulse gap-4 px-5 py-3">
                  <div className="h-3 w-28 rounded bg-bg-tertiary" />
                  <div className="h-3 w-40 rounded bg-bg-tertiary" />
                  <div className="h-3 flex-1 rounded bg-bg-tertiary" />
                </li>
              ))}
            </ul>
          ) : events.length === 0 ? (
            <Empty>{hasFilters ? "По фильтрам ничего не найдено" : "Событий нет"}</Empty>
          ) : (
            <ul className={`divide-y divide-border-primary ${state === "loading" ? "opacity-60" : ""}`}>
              {events.map((e, i) => (
                <EventRow key={e.id || `${e.timestamp}-${i}`} event={e} />
              ))}
            </ul>
          )}
          {events.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border-primary px-5 py-3 text-xs text-text-secondary">
              <span>
                {fmt(offset + 1)}–{fmt(Math.min(offset + limit, total))} из {fmt(total)}
              </span>
              <div className="flex items-center gap-2">
                <select value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }} aria-label="На странице" className="rounded-lg border border-border-primary bg-bg-primary px-2 py-1 text-xs text-text-primary">
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>{n} на странице</option>
                  ))}
                </select>
                <button type="button" onClick={() => setOffset((o) => Math.max(0, o - limit))} disabled={offset === 0} aria-label="Назад" className="rounded-lg border border-border-primary p-1.5 hover:bg-bg-tertiary disabled:opacity-40">
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
                <button type="button" onClick={() => setOffset((o) => o + limit)} disabled={offset + limit >= total} aria-label="Вперёд" className="rounded-lg border border-border-primary p-1.5 hover:bg-bg-tertiary disabled:opacity-40">
                  <ChevronRightIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default EventsList;
